import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  formatItemAmount,
  importFromProcore,
  listFromProcore,
  loadActiveProcoreIntegrations,
  retrieveLabel,
  sendToProcore,
} from './procoreActions';
import { integrationError } from './helpers';

const ProcoreIntegrationContext = createContext({
  integrations: [],
  loading: true,
});

export function ProcoreIntegrationProvider({ children }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActiveProcoreIntegrations()
      .then(setIntegrations)
      .catch(() => setIntegrations([]))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo(() => ({ integrations, loading }), [integrations, loading]);
  return (
    <ProcoreIntegrationContext.Provider value={value}>
      {children}
    </ProcoreIntegrationContext.Provider>
  );
}

export default function IntegrationActions({
  projectVuid,
  retrieveKind,
  retrieveKinds,
  sendKind,
  sendPayload,
  sendLabel,
  compact = false,
  onComplete,
}) {
  const { integrations, loading } = useContext(ProcoreIntegrationContext);
  const [selectedVuid, setSelectedVuid] = useState('');
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (!selectedVuid && integrations.length) {
      setSelectedVuid(integrations[0].vuid);
    }
  }, [integrations, selectedVuid]);

  const kinds = retrieveKinds || (retrieveKind ? [retrieveKind] : []);
  const integration = integrations.find((row) => row.vuid === selectedVuid) || integrations[0];
  const disabled = loading || !integration || Boolean(busy);
  const sendText = sendLabel || (compact ? 'Send' : 'Send to Procore');

  const showFlash = (type, text) => {
    setFlash({ type, text });
  };

  const runSend = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!integration || !sendKind) return;
    setBusy('send');
    setFlash(null);
    try {
      const data = await sendToProcore(integration.vuid, sendKind, sendPayload);
      showFlash('ok', data?.message || `Sent to ${integration.integration_name}.`);
      onComplete?.();
    } catch (error) {
      showFlash('err', integrationError(error));
    } finally {
      setBusy('');
    }
  };

  const runRetrieve = async (kind) => {
    if (!integration) return;
    setBusy(kind);
    setFlash(null);
    try {
      const { items, warnings } = await listFromProcore(kind, integration.vuid, projectVuid);
      if (!items.length) {
        const extra = warnings.length ? `\n${warnings.slice(0, 5).join('\n')}` : '';
        showFlash('err', `No records found in Procore.${extra}`);
        return;
      }
      setModal({
        kind,
        items,
        selected: items.filter((row) => !row.alreadyImported).map((row) => row.id),
        warnings,
      });
    } catch (error) {
      showFlash('err', integrationError(error));
    } finally {
      setBusy('');
    }
  };

  const toggleItem = (id) => {
    setModal((current) => {
      if (!current) return current;
      const selected = current.selected.includes(id)
        ? current.selected.filter((value) => value !== id)
        : [...current.selected, id];
      return { ...current, selected };
    });
  };

  const runImport = async () => {
    if (!modal || !integration) return;
    const selected = modal.items.filter((row) => modal.selected.includes(row.id) && !row.alreadyImported);
    if (!selected.length) {
      showFlash('err', 'Select at least one record to import.');
      return;
    }
    setBusy('import');
    try {
      const result = await importFromProcore(modal.kind, integration.vuid, projectVuid, selected);
      const extra = (result.errors || []).length
        ? `\n${result.errors.slice(0, 6).join('\n')}`
        : '';
      showFlash(result.errors?.length ? 'err' : 'ok', `${result.message}${extra}`);
      setModal(null);
      onComplete?.();
    } catch (error) {
      showFlash('err', integrationError(error));
    } finally {
      setBusy('');
    }
  };

  if (!kinds.length && !sendKind) return null;

  return (
    <div className={`pc-actions ${compact ? 'pc-actions-compact' : ''}`} onClick={(event) => event.stopPropagation()}>
      {!compact && integrations.length > 1 ? (
        <select
          className="pc-select"
          value={integration?.vuid || ''}
          onChange={(event) => setSelectedVuid(event.target.value)}
          aria-label="Procore integration"
        >
          {integrations.map((row) => (
            <option key={row.vuid} value={row.vuid}>
              {row.integration_name}
            </option>
          ))}
        </select>
      ) : null}
      {kinds.map((kind) => (
        <button
          key={kind}
          type="button"
          className={compact ? 'pc-btn pc-btn-secondary pc-btn-sm' : 'pc-btn pc-btn-secondary'}
          disabled={disabled}
          onClick={() => runRetrieve(kind)}
        >
          {busy === kind ? 'Retrieving…' : retrieveLabel(kind)}
        </button>
      ))}
      {sendKind ? (
        <button
          type="button"
          className={compact ? 'pc-btn pc-btn-sm' : 'pc-btn'}
          disabled={disabled}
          onClick={runSend}
        >
          {busy === 'send' ? 'Sending…' : sendText}
        </button>
      ) : null}
      {!compact && !loading && !integrations.length ? (
        <span className="pc-flash-inline">No active Procore integration</span>
      ) : null}
      {!compact && flash ? (
        <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
      ) : null}
      {compact && flash ? (
        <span className={`pc-flash-inline ${flash.type === 'ok' ? 'ok' : 'err'}`}>{flash.text}</span>
      ) : null}
      {modal ? (
        <div className="pc-modal-backdrop" onClick={() => !busy && setModal(null)}>
          <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pc-modal-head">
              <h2>{retrieveLabel(modal.kind)}</h2>
              <button type="button" className="pc-modal-close" onClick={() => setModal(null)} aria-label="Close">
                ×
              </button>
            </div>
            {modal.warnings?.length ? (
              <p className="pc-modal-project pc-flash-err" style={{ whiteSpace: 'pre-wrap' }}>
                {modal.warnings.slice(0, 4).join('\n')}
              </p>
            ) : null}
            <div className="pc-import-toolbar">
              <button
                type="button"
                className="pc-btn pc-btn-secondary pc-btn-sm"
                onClick={() =>
                  setModal((current) => ({
                    ...current,
                    selected: current.items.filter((row) => !row.alreadyImported).map((row) => row.id),
                  }))
                }
              >
                Select all
              </button>
              <button
                type="button"
                className="pc-btn pc-btn-secondary pc-btn-sm"
                onClick={() => setModal((current) => ({ ...current, selected: [] }))}
              >
                Clear
              </button>
              <span className="pc-muted">
                {modal.selected.length} selected
              </span>
            </div>
            <div className="pc-import-list">
              {modal.items.map((row) => (
                <label key={row.id} className={`pc-import-row ${row.alreadyImported ? 'is-imported' : ''}`}>
                  <input
                    type="checkbox"
                    checked={modal.selected.includes(row.id)}
                    disabled={row.alreadyImported}
                    onChange={() => toggleItem(row.id)}
                  />
                  <span>
                    <strong>{row.title}</strong>
                    <span className="pc-meta">
                      {[row.subtitle, formatItemAmount(row.amount), row.alreadyImported ? 'Already imported' : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="pc-modal-actions">
              <button type="button" className="pc-btn pc-btn-secondary" onClick={() => setModal(null)} disabled={Boolean(busy)}>
                Cancel
              </button>
              <button type="button" className="pc-btn" onClick={runImport} disabled={Boolean(busy) || !modal.selected.length}>
                {busy === 'import' ? 'Importing…' : `Import ${modal.selected.length}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
